import {
  BuildTreeParams,
  TreeCompoundIndex,
  TreeManager,
} from '@basehub/utils/tree/index'
import { Block, BlockType } from '.'
import {
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  isReferenceBlock,
  isReferenceableBlock,
  isRootBlock,
} from '../type-casts'
import { IndexedMap } from '@basehub/utils/indexed-map/index'
import {
  BlockRelationship,
  CommonBRsCompoundIndexes,
  compareOrder,
} from '../block-relationship'
import { invariant } from '@epic-web/invariant'
import {
  isVirtualInstanceChildTarget,
  isVirtualizedInstanceChildTargetBR,
  makeInstanceChildBlock,
  makeVirtualizedInstanceChildBR,
} from './instance'
import { injectBuiltInAgents } from './agents'

/* -------------------------------------------------------------------------------------------------
 * Common Compound Indexes
 * -----------------------------------------------------------------------------------------------*/

export type CommonBlocksCompoundIndexes = {
  components: TreeCompoundIndex<Block, Set<string>>
  blocksByType: TreeCompoundIndex<Block, Map<BlockType, Set<string>>>
  referenceable: TreeCompoundIndex<Block, Set<string>>
  componentInstances: TreeCompoundIndex<Block, Map<string, Set<string>>>
  componentCollections: TreeCompoundIndex<Block, Map<string, Set<string>>>
  referencesByTarget: TreeCompoundIndex<Block, Map<string, Set<string>>>
  byInstanceChildTarget: TreeCompoundIndex<Block, Map<string, Set<string>>>
}

export const components = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    if (
      !isComponentBlock(node.value) &&
      (!oldNode || !isComponentBlock(oldNode.value))
    ) {
      return currentValue ?? new Set<string>()
    }
    const current = currentValue ?? new Set<string>()

    switch (operation) {
      case 'upsert':
        current.add(node.key)
        break
      case 'delete':
        current.delete(node.key)
        break
    }

    if (!isComponentBlock(node.value)) {
      // delete from index if type changed
      current.delete(node.key)
    }

    return current
  },
  cloneIndex: (currentValue) => new Set(currentValue),
} satisfies CommonBlocksCompoundIndexes['components']

export const blocksByType = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    const current = currentValue ?? new Map<BlockType, Set<string>>()

    // Remove from old type if node type changed
    if (oldNode && oldNode.value) {
      if (!current.has(oldNode.value.type)) {
        current.set(oldNode.value.type, new Set())
      }
      const oldSet = current.get(oldNode.value.type)!
      oldSet.delete(oldNode.key)
    }

    // Add to current type
    if (node.value) {
      if (!current.has(node.value.type)) {
        current.set(node.value.type, new Set())
      }
      const currentSet = current.get(node.value.type)!

      if (operation === 'upsert') {
        currentSet.add(node.key)
      } else {
        currentSet.delete(node.key)
      }
    }

    return current
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<BlockType, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBlocksCompoundIndexes['blocksByType']

export const referenceable = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    if (
      !isReferenceableBlock(node.value) &&
      (!oldNode || !isReferenceableBlock(oldNode.value))
    ) {
      return currentValue ?? new Set<string>()
    }
    const current = currentValue ?? new Set<string>()

    switch (operation) {
      case 'upsert':
        current.add(node.key)
        break
      case 'delete':
        current.delete(node.key)
        break
    }

    if (!isReferenceableBlock(node.value)) {
      // delete from index if type changed
      current.delete(node.key)
    }

    return current
  },
  cloneIndex: (currentValue) => new Set(currentValue),
} satisfies CommonBlocksCompoundIndexes['referenceable']

export const componentInstances = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    if (
      !isInstanceBlock(node.value) &&
      (!oldNode || !isInstanceBlock(oldNode.value))
    ) {
      return currentValue ?? new Map<string, Set<string>>()
    }

    const current = currentValue ?? new Map<string, Set<string>>()

    if (isInstanceBlock(node.value)) {
      const targetId = node.value.typeOptions.targetBlockId
      if (!current.has(targetId)) {
        current.set(targetId, new Set())
      }
      const currentSet = current.get(targetId)!

      switch (operation) {
        case 'upsert':
          currentSet.add(node.key)
          break
        case 'delete':
          currentSet.delete(node.key)
          break
      }
    } else if (oldNode && isInstanceBlock(oldNode.value)) {
      const targetId = oldNode.value.typeOptions.targetBlockId
      if (!current.has(targetId)) {
        current.set(targetId, new Set())
      }
      const currentSet = current.get(targetId)!
      currentSet.delete(oldNode.key)
    }

    return current
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBlocksCompoundIndexes['componentInstances']

export const componentCollections = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    if (!isListBlock(node.value) && (!oldNode || !isListBlock(oldNode.value))) {
      return currentValue ?? new Map<string, Set<string>>()
    }

    const current = currentValue ?? new Map<string, Set<string>>()

    if (isListBlock(node.value)) {
      const targetId = node.value.typeOptions.targetBlockId
      if (!current.has(targetId)) {
        current.set(targetId, new Set())
      }
      const currentSet = current.get(targetId)!

      switch (operation) {
        case 'upsert':
          currentSet.add(node.key)
          break
        case 'delete':
          currentSet.delete(node.key)
          break
      }
    } else if (oldNode && isListBlock(oldNode.value)) {
      const targetId = oldNode.value.typeOptions.targetBlockId
      if (!current.has(targetId)) {
        current.set(targetId, new Set())
      }
      const currentSet = current.get(targetId)!
      currentSet.delete(oldNode.key)
    }

    return current
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBlocksCompoundIndexes['componentCollections']

export const referencesByTarget = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    if (
      !isReferenceBlock(node.value) &&
      (!oldNode || !isReferenceBlock(oldNode.value))
    ) {
      return currentValue ?? new Map<string, Set<string>>()
    }

    const current = currentValue ?? new Map<string, Set<string>>()

    if (isReferenceBlock(node.value)) {
      node.value.value.targetBlockIds.forEach((targetId) => {
        if (!current.has(targetId)) {
          current.set(targetId, new Set())
        }
        const currentSet = current.get(targetId)!

        switch (operation) {
          case 'upsert':
            currentSet.add(node.key)
            break
          case 'delete':
            currentSet.delete(node.key)
            break
        }
      })
    } else if (oldNode && isReferenceBlock(oldNode.value)) {
      oldNode.value.value.targetBlockIds.forEach((targetId) => {
        if (!current.has(targetId)) {
          current.set(targetId, new Set())
        }
        const currentSet = current.get(targetId)!
        currentSet.delete(oldNode.key)
      })
    }

    return current
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBlocksCompoundIndexes['referencesByTarget']

export const byInstanceChildTarget = {
  updateIndex: ({ node, oldNode, currentValue, operation }) => {
    const current = currentValue ?? new Map<string, Set<string>>()

    // Handle regular instanceChildTarget
    if (node.value.instanceChildTarget) {
      if (!current.has(node.value.instanceChildTarget)) {
        current.set(node.value.instanceChildTarget, new Set())
      }
      const set = current.get(node.value.instanceChildTarget)!

      if (operation === 'upsert') {
        set.add(node.key)
      } else {
        set.delete(node.key)
      }
    }

    // Handle virtual blocks' __virtualInstanceChildTarget
    if (isVirtualInstanceChildTarget(node.value)) {
      const virtualTarget = node.value.__virtualInstanceChildTarget
      if (virtualTarget) {
        if (!current.has(virtualTarget)) {
          current.set(virtualTarget, new Set())
        }
        const set = current.get(virtualTarget)!

        if (operation === 'upsert') {
          set.add(node.key)
        } else {
          set.delete(node.key)
        }
      }
    }

    // Clean up old targets
    if (
      oldNode?.value.instanceChildTarget &&
      oldNode.value.instanceChildTarget !== node.value.instanceChildTarget
    ) {
      if (!current.has(oldNode.value.instanceChildTarget)) {
        current.set(oldNode.value.instanceChildTarget, new Set())
      }
      const oldSet = current.get(oldNode.value.instanceChildTarget)!
      oldSet.delete(oldNode.key)
    }

    if (oldNode && isVirtualInstanceChildTarget(oldNode.value)) {
      const oldVirtualTarget = oldNode.value.__virtualInstanceChildTarget
      if (
        oldVirtualTarget &&
        (!isVirtualInstanceChildTarget(node.value) ||
          node.value.__virtualInstanceChildTarget !== oldVirtualTarget)
      ) {
        if (!current.has(oldVirtualTarget)) {
          current.set(oldVirtualTarget, new Set())
        }
        const oldSet = current.get(oldVirtualTarget)!
        oldSet.delete(oldNode.key)
      }
    }

    return current
  },
  cloneIndex: (currentValue) => {
    const cloned = new Map<string, Set<string>>()
    for (const [key, set] of currentValue.entries()) {
      cloned.set(key, new Set(set))
    }
    return cloned
  },
} satisfies CommonBlocksCompoundIndexes['byInstanceChildTarget']

export const commonBlocksCompoundIndexes = {
  components,
  blocksByType,
  referenceable,
  componentInstances,
  componentCollections,
  referencesByTarget,
  byInstanceChildTarget,
} satisfies CommonBlocksCompoundIndexes

/* -------------------------------------------------------------------------------------------------
 * Get Children Keys (handles virtualizing instance children)
 * -----------------------------------------------------------------------------------------------*/

const idToSourceBlockId = (block: Block) => block.sourceBlockId || block.id

/* -------------------------------------------------------------------------------------------------
 * Instance Chain Tracking (for detecting circular instance references)
 * -----------------------------------------------------------------------------------------------*/

/**
 * Tracks instance chains during getChildrenKeys to detect infinite loops.
 * Uses a combination of:
 * 1. targetBlockId chain - detects when the same component is being virtualized again in the chain
 * 2. Depth limit - catches runaway recursion even without exact cycle detection
 *
 * Key insight: A real infinite loop occurs when we're virtualizing the SAME targetBlockId
 * that we're already in the process of virtualizing (ancestor in the call stack).
 */
let _instanceTargetChain: Set<string> | null = null
let _instanceChainDepth = 0
const MAX_INSTANCE_CHAIN_DEPTH = 50 // Must be low enough to prevent stack overflow

export function getChildrenKeys({
  rootBlockId,
  block,
  blockRelationshipsMap,
  blocksMap,
  omitBuiltInAgents,
}: {
  rootBlockId: string
  block: Block
  blockRelationshipsMap: IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  blocksMap: IndexedMap<Block>
  omitBuiltInAgents?: boolean
}): string[] {
  const debug =
    // idToSourceBlockId(block) === '3xycnsxObIwD1umN4vRdp' ||
    // idToSourceBlockId(block) === '5ecd8d6c2fb396e0e6a98' ||
    idToSourceBlockId(block) === '66f40ce82144dc7cd17ab'
  // || idToSourceBlockId(block) === 'BAS4ZIPdBckf3s8uXPt9e'

  if (debug) {
    console.log('——————————')
    console.log('debugging', block)
  }

  if (isInstanceBlock(block)) {
    const targetId = block.typeOptions.targetBlockId

    // Initialize chain tracking if this is the first instance in the chain
    const isChainRoot = _instanceTargetChain === null
    if (isChainRoot) {
      _instanceTargetChain = new Set<string>()
      _instanceChainDepth = 0
    }

    _instanceChainDepth++

    // Check for self-referential component: same targetBlockId already being virtualized up the chain
    // This is a definitive infinite loop - a component containing an instance of itself
    if (_instanceTargetChain!.has(targetId)) {
      console.error(
        `[TreeManager] Circular instance reference detected: Component "${targetId}" contains an instance that targets itself.`,
        {
          block: { id: block.id, title: block.title, apiName: block.apiName },
          chainDepth: _instanceChainDepth,
        }
      )

      // Cleanup and return empty to stop recursion
      _instanceChainDepth--
      if (isChainRoot) {
        _instanceTargetChain = null
        _instanceChainDepth = 0
      }
      return []
    }

    // Check for excessive depth (catches cycles we might have missed)
    if (_instanceChainDepth > MAX_INSTANCE_CHAIN_DEPTH) {
      console.error(
        `[TreeManager] Instance chain depth exceeded ${MAX_INSTANCE_CHAIN_DEPTH}. Possible infinite loop.`,
        {
          block: { id: block.id, title: block.title, apiName: block.apiName },
          targetBlockId: targetId,
        }
      )

      _instanceChainDepth--
      if (isChainRoot) {
        _instanceTargetChain = null
        _instanceChainDepth = 0
      }
      return []
    }

    // Track this targetBlockId for cycle detection in nested calls
    _instanceTargetChain!.add(targetId)

    // Cleanup function for chain tracking - ensures cleanup happens even if an exception is thrown
    const cleanup = () => {
      _instanceTargetChain?.delete(targetId)
      _instanceChainDepth--
      if (isChainRoot) {
        _instanceTargetChain = null
        _instanceChainDepth = 0
      }
    }

    try {
    // build children based on target, virtualizing children that might be missing
    let targetBR = blockRelationshipsMap.get(block.typeOptions.targetBlockId)
    if (!targetBR) {
      /**
       * try to patch a bug we had where targetBlockId had a direct id instead of sourceBlockId
       */
      const instanceBr = blockRelationshipsMap.get(block.id)
      if (instanceBr) {
        const parentBlock = blocksMap.get(instanceBr.parentId)
        if (parentBlock && parentBlock.type === 'list') {
          // assume the targetBlockId is the same as the list's
          const targetBRList = blockRelationshipsMap.get(
            parentBlock.typeOptions.targetBlockId
          )
          if (targetBRList) {
            targetBR = targetBRList
            block.typeOptions.targetBlockId =
              parentBlock.typeOptions.targetBlockId
          }
        }
      }
    }
    // A dangling target is an unresolved component, same as a missing target block
    if (!targetBR) {
      return []
    }
    const targetBlock = blocksMap.get(targetBR.blockId)
    if (!targetBlock) {
      return []
    }
    invariant(targetBlock, `targetBlock ${targetBR.blockId} not found`)

    const debug = false

    // in the case of nested instances, we want to use the instanceChildTarget as well to inherit the correct default values
    let childTargetBlock: Block | null = null
    if (
      block.instanceChildTarget &&
      block.instanceChildTarget !== block.typeOptions.targetBlockId
    ) {
      const childTargetBR = blockRelationshipsMap.get(block.instanceChildTarget)
      if (childTargetBR) {
        childTargetBlock = blocksMap.get(childTargetBR.blockId)
        invariant(
          childTargetBlock,
          `childTargetBlock ${childTargetBR.blockId} not found`
        )
      }
    }

    const childBlocks = (
      Array.from(
        blockRelationshipsMap.compoundIndexes
          .get('byParentId')
          ?.get(idToSourceBlockId(block)) ?? []
      ).map((brId) => {
        const br = blockRelationshipsMap.get(brId)
        if (!br) return null
        if (isVirtualizedInstanceChildTargetBR(br)) return null
        if (br.deletedAt) return null
        if (br.placeholderSince) return null
        const childBlock = blocksMap.get(br.blockId)
        if (!childBlock) return null
        if (isVirtualInstanceChildTarget(childBlock)) return null
        if (!childBlock.instanceChildTarget) return null
        if (childBlock.type === 'comment') return null
        return childBlock
      }) ?? []
    ).filter(Boolean)

    const targetChildBRs = blockRelationshipsMap.compoundIndexes
      .get('byParentId')
      ?.get(idToSourceBlockId(targetBlock))

    const childTargetChildBlocks = childTargetBlock
      ? Array.from(
          blockRelationshipsMap.compoundIndexes
            .get('byParentId')
            ?.get(idToSourceBlockId(childTargetBlock)) ?? []
        )
          .map((brId) => {
            const br = blockRelationshipsMap.get(brId)
            invariant(br, `block relationship ${brId} not found in cache`)
            if (br.deletedAt) return null
            if (br.placeholderSince) return null
            if (isVirtualizedInstanceChildTargetBR(br)) return null
            const childBlock = blocksMap.get(br.blockId)
            if (!childBlock) {
              // One line with IDs: this runs per instance on every tree
              // build, and dumping the blocks flooded the logs
              // eslint-disable-next-line no-console
              console.warn(
                `child block ${br.blockId} not found in cache (instance ${block.id}, child target ${childTargetBlock.id}, target ${targetBlock.id})`
              )
              return null
            }
            invariant(
              childBlock,
              `child target child block ${br.blockId} not found in cache`
            )
            if (isVirtualInstanceChildTarget(childBlock)) return null
            return childBlock
          })
          .filter(Boolean)
      : null

    if (debug) {
      // eslint-disable-next-line no-console
      console.log({
        br: blockRelationshipsMap.get(block.id),
        childTargetBlock,
        childTargetChildBlocks,
        childBlocks,
        targetBlock,
        block,
      })
    }

    if (!targetChildBRs) return []
    return Array.from(targetChildBRs)
      .map((brId) => {
        const br = blockRelationshipsMap.get(brId)
        if (!br) {
          console.error({ brId, block, blockRelationshipsMap })
        }
        invariant(br, `block relationship ${brId} not found in cache`)
        if (br.deletedAt) return null
        if (br.placeholderSince) return null
        const targetChild = blocksMap.get(br.blockId)
        if (!targetChild) return null
        if (targetChild.type === 'comment') return null
        if (targetChild.deletedAt) return null

        return br
      })
      .filter(Boolean)
      .sort(compareOrder)
      .map((br) => {
        let childTargetChild = childTargetChildBlocks?.find(
          (b) => b.instanceChildTarget === br.sourceBlockId
        )
        const targetChild = blocksMap.get(br.blockId)
        invariant(targetChild, `targetChild ${br.blockId} not found in cache`)

        if (childTargetChild && childTargetBlock) {
          childTargetChild = makeInstanceChildBlock({
            block: childTargetChild,
            parentSourceBlockId: idToSourceBlockId(childTargetBlock),
            targetBlock: targetChild,
          })
        }

        const childMatch = childBlocks.find(
          (b) =>
            b.instanceChildTarget === br.sourceBlockId ||
            (childTargetChild &&
              (b.instanceChildTarget === idToSourceBlockId(childTargetChild) ||
                b.instanceChildTarget === childTargetChild.instanceChildTarget))
        )

        const enhanced = makeInstanceChildBlock({
          block: childMatch ?? null,
          parentSourceBlockId: idToSourceBlockId(block),
          targetBlock: childTargetChild ?? targetChild,
        })

        blocksMap.set(enhanced.id, enhanced)
        const existingBr = blockRelationshipsMap.get(enhanced.id)
        if (
          !existingBr ||
          // the virtualized br copies the target's order, which changes when
          // the component's fields are reordered
          (isVirtualizedInstanceChildTargetBR(existingBr) &&
            existingBr.order !== br.order)
        ) {
          // create the br for the virtualized block
          const newBr = makeVirtualizedInstanceChildBR({
            parentBlock: block,
            virtualizedBlock: enhanced,
            targetBR: br,
            rootBlockId,
          })
          blockRelationshipsMap.set(newBr.id, newBr)
        }

        return enhanced.id
      })
    } finally {
      cleanup()
    }
  } else {
    let injected: Set<string> | null = null
    if (isRootBlock(block) && !omitBuiltInAgents) {
      // inject built-in agents to the root block children
      injected = injectBuiltInAgents({
        blockRelationshipsMap,
        blocksMap,
        rootBlock: block,
      })
    }
    // regular case
    const childBRs = blockRelationshipsMap.compoundIndexes
      .get('byParentId')
      ?.get(idToSourceBlockId(block))

    if (debug) {
      console.log(JSON.stringify(Array.from(childBRs ?? new Set()), null, 2))
    }

    if (!childBRs) return []
    if (injected) {
      injected.forEach((id) => {
        if (!childBRs.has(id)) {
          childBRs.add(id)
        }
      })
    }
    return Array.from(childBRs)
      .map((brId) => {
        const br = blockRelationshipsMap.get(brId)
        if (!br) {
          console.error({ brId, block, blockRelationshipsMap })
        }
        invariant(br, `block relationship ${brId} not found in cache`)
        if (br.deletedAt) return null
        if (br.placeholderSince) return null
        const childBlock = blocksMap.get(br.blockId)
        if (!childBlock || childBlock.deletedAt) return null
        if (block.type === 'list') {
          if (
            childBlock?.type !== 'component' &&
            childBlock?.type !== 'instance'
          ) {
            return null
          }
        } else if (block.type === 'component') {
          if (childBlock.instanceChildTarget) {
            blocksMap.set(childBlock.id, {
              ...childBlock,
              instanceChildTarget: undefined,
            })
          }
        }
        if (debug) {
          console.log('returing', brId)
          console.log({ br, childBlock })
        }
        return br
      })
      .filter(Boolean)
      .sort(compareOrder)
      .map((br) => br.blockId)
  }
}

/* -------------------------------------------------------------------------------------------------
 * Get On Change Handler
 * -----------------------------------------------------------------------------------------------*/

/**
 * An instance's children are built from its component's fields: virtual copies,
 * plus overrides where someone edited a field. When a field is deleted from (or
 * moved out of) its component, or an instance is pointed at another component,
 * no patch removes them: copies have no BRs, and an override's BR stays (the
 * override comes back if the field does). Deletes the instance children among
 * `keys` that a fresh build wouldn't put under their parent anymore
 */
const pruneStaleInstanceChildren = (
  keys: Iterable<string>,
  tree: TreeManager<Block, CommonBlocksCompoundIndexes>
) => {
  const expectedChildrenByParent = new Map<string, Set<string> | null>()
  for (const key of [...keys]) {
    const node = tree.getNode(key, { fallbackKey: false })
    if (!node?.parentKey || !node.value.instanceChildTarget) continue

    let expected = expectedChildrenByParent.get(node.parentKey)
    if (expected === undefined) {
      try {
        expected = new Set(
          tree.getChildrenKeys(node.parentKey, { cache: false })
        )
      } catch {
        // can't tell what belongs there, so leave it
        expected = null
      }
      expectedChildrenByParent.set(node.parentKey, expected)
    }
    if (!expected || expected.has(key)) continue

    deleteSubtree(key, tree)
  }
}

const deleteSubtree = (
  key: string,
  tree: TreeManager<Block, CommonBlocksCompoundIndexes>
) => {
  let childrenKeys: string[] = []
  try {
    childrenKeys = tree.getChildrenKeys(key, { cache: false })
  } catch {
    // e.g. an instance whose target is gone: nothing was built under it
  }
  tree.deleteNode(key, { skipOnChange: true })
  for (const childKey of childrenKeys) {
    if (tree.getNode(childKey, { fallbackKey: false })?.parentKey === key) {
      deleteSubtree(childKey, tree)
    }
  }
}

const revalidateInstance = (
  instanceId: string,
  tree: TreeManager<Block, CommonBlocksCompoundIndexes>,
  blockRelationshipsMap: IndexedMap<
    BlockRelationship,
    CommonBRsCompoundIndexes
  >,
  blocksMap: IndexedMap<Block>,
  depth: number
) => {
  // Prevent infinite recursion
  if (depth > 300) {
    console.warn(
      `Maximum recursion depth reached for virtual block: ${instanceId}`
    )
    return
  }

  try {
    const instanceNode = tree.getNode(instanceId, {
      required: true,
    })

    if (!isInstanceBlock(instanceNode.value)) return

    // Create virtual blocks in the underlying maps by calling getChildrenKeys
    const children = getChildrenKeys({
      rootBlockId: tree.root.value.id,
      block: instanceNode.value,
      blockRelationshipsMap,
      blocksMap,
    })

    // Register virtual blocks with TreeManager if they don't already exist
    children.forEach((childId) => {
      const childBlock = blocksMap.get(childId)
      const childBR = blockRelationshipsMap.get(childId)
      if (!childBlock || !childBR) return
      if (!childBlock.instanceChildTarget) return
      const existing = tree.getNode(childBlock.id, { fallbackKey: false })

      if (!existing) {
        tree.addNode(
          childBlock,
          {
            parentKey: instanceNode.key,
            order: childBR.order,
          },
          { skipOnChange: true }
        )
      } else {
        tree.updateNode(
          childBlock.id,
          {
            value: childBlock,
            order: childBR.order,
            parentKey: instanceNode.key,
          },
          { skipOnChange: true }
        )
      }

      if (childBlock.type === 'instance') {
        revalidateInstance(
          childBlock.id,
          tree,
          blockRelationshipsMap,
          blocksMap,
          depth + 1
        )
      }
    })

    // Invalidate cache to ensure fresh computation
    tree.childrenKeysCache.delete(instanceId)
  } catch (e) {
    // Instance might have been deleted, ignore errors
    return
  }
}

export const getOnChangeHandler = ({
  blockRelationshipsMap,
  blocksMap,
}: {
  blockRelationshipsMap: IndexedMap<BlockRelationship, CommonBRsCompoundIndexes>
  blocksMap: IndexedMap<Block>
}): NonNullable<
  BuildTreeParams<Block, CommonBlocksCompoundIndexes>['onChange']
> => {
  return ({ node, oldNode, operation, tree }) => {
    const parentItem = node.parentKey
      ? tree.getNode(node.parentKey)?.value
      : null
    const instancesToRevalidate = new Set<string>()
    const maybeStaleInstanceChildren = new Set<string>()

    const byInstanceChildTarget = tree.compoundIndexes.get(
      'byInstanceChildTarget'
    )
    const componentInstancesIndex =
      tree.compoundIndexes.get('componentInstances')

    // Handle instance updates when their component changes
    if (parentItem?.type === 'component') {
      if (!componentInstancesIndex) return

      const instances = componentInstancesIndex.get(
        idToSourceBlockId(parentItem)
      )
      if (instances && instances.size > 0) {
        instances.forEach((instanceId) => {
          instancesToRevalidate.add(instanceId)
        })
      }
    } else {
      let path: string[] | null = null
      try {
        path = tree.getPath(node, { format: 'array' })
      } catch (error) {
        // Block might have been deleted from tree. If it was an instance child,
        // revalidate its parent instance to recreate virtual children.
        if (node.value.instanceChildTarget && node.parentKey) {
          const parentNode = tree.getNode(node.parentKey, {
            fallbackKey: 'sourceBlockId',
          })
          if (parentNode && isInstanceBlock(parentNode.value)) {
            instancesToRevalidate.add(parentNode.key)
          }
        }
      }

      if (!path) {
        // Same fallback when getPath couldn't resolve ancestry but didn't throw.
        if (node.value.instanceChildTarget && node.parentKey) {
          const parentNode = tree.getNode(node.parentKey, {
            fallbackKey: 'sourceBlockId',
          })
          if (parentNode && isInstanceBlock(parentNode.value)) {
            instancesToRevalidate.add(parentNode.key)
          }
        }
      }

      let someAncestorIsComponent = false
      for (const segment of [...(path ?? [])].reverse()) {
        const segmentNode = tree.getNode(segment)
        if (!segmentNode) break
        if (segmentNode.value.type === 'component') {
          someAncestorIsComponent = true
          break
        }
        if (segmentNode.value.type !== 'instance') {
          // if parent is not instance (and not component as we checked above), the tight relationship should stop
          break
        }
      }

      if (someAncestorIsComponent) {
        const instanceChildTargettingSameTargetAsMe = node.value
          .instanceChildTarget
          ? byInstanceChildTarget?.get(node.value.instanceChildTarget)
          : null
        if (
          instanceChildTargettingSameTargetAsMe &&
          instanceChildTargettingSameTargetAsMe.size > 0
        ) {
          instanceChildTargettingSameTargetAsMe.forEach((childId) => {
            if (childId === node.key) return
            const instanceChild = tree.getNode(childId)
            if (instanceChild?.parentKey) {
              instancesToRevalidate.add(instanceChild.parentKey)
            }
          })
        }
      }
    }

    const instanceChildTargettingMe = byInstanceChildTarget?.get(
      idToSourceBlockId(node.value)
    )
    if (instanceChildTargettingMe && instanceChildTargettingMe.size > 0) {
      instanceChildTargettingMe.forEach((childId) => {
        const instanceChild = tree.getNode(childId)
        if (instanceChild?.parentKey) {
          instancesToRevalidate.add(instanceChild.parentKey)
        }
      })
    }

    // Handle instance block updates
    if (isInstanceBlock(node.value) && operation === 'upsert') {
      instancesToRevalidate.add(node.key)
    }

    // A field that was deleted or moved: its copies and overrides in the
    // instances of the component it left are stale
    if (
      operation === 'delete' ||
      (oldNode && oldNode.parentKey !== node.parentKey)
    ) {
      byInstanceChildTarget
        ?.get(idToSourceBlockId(node.value))
        ?.forEach((key) => maybeStaleInstanceChildren.add(key))
    }

    // An instance that points at another component, or isn't an instance
    // anymore: its children were built from the old target. Rare, so a scan
    // is fine
    if (
      operation === 'upsert' &&
      oldNode &&
      isInstanceBlock(oldNode.value) &&
      (!isInstanceBlock(node.value) ||
        node.value.typeOptions.targetBlockId !==
          oldNode.value.typeOptions.targetBlockId)
    ) {
      for (const child of tree.getAllNodes()) {
        if (child.parentKey === node.key) {
          maybeStaleInstanceChildren.add(child.key)
        }
      }
    }

    // Process all instances that need updating
    // Create virtual blocks and register them with TreeManager
    instancesToRevalidate.forEach((instanceId) => {
      revalidateInstance(instanceId, tree, blockRelationshipsMap, blocksMap, 0)
    })

    pruneStaleInstanceChildren(maybeStaleInstanceChildren, tree)

    if (operation === 'upsert' && node.value.instanceChildTarget) {
      const targetNode = tree.getNode(node.value.instanceChildTarget)
      if (targetNode) {
        if (!parentItem) {
          if (isVirtualInstanceChildTarget(node.value)) return
          return
        }
        invariant(parentItem, 'parentItem not found')
        const enhanced = makeInstanceChildBlock({
          block: node.value,
          parentSourceBlockId: idToSourceBlockId(parentItem),
          targetBlock: targetNode.value,
        })
        node.value = enhanced
      }
    }
  }
}
